import React, { useEffect, useState, useRef } from "react";
import classes from './AssetMovement.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X, FileText, Calendar, Move, MapPin, ArrowRight, ArrowLeft } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const AssetMovement = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_movements: 0,
        unique_assets: 0,
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [assetMovements, setAssetMovements] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewEditModal, setShowViewEditModal] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedMovement, setSelectedMovement] = useState(null);

    // Create Modal state
    const [assets, setAssets] = useState([]);
    const [locations, setLocations] = useState([]);
    const [currentAssetLocation, setCurrentAssetLocation] = useState(null);
    const [createFormData, setCreateFormData] = useState({
        asset_id: "",
        from_location_id: "",
        to_location_id: "",
        movement_date: "",
        company_id: ""
    });
    const [createLoading, setCreateLoading] = useState({
        assets: false,
        locations: false
    });
    const [submitting, setSubmitting] = useState(false);
    const [locationError, setLocationError] = useState("");

    // For searchable selects in create modal
    const [searchTerms, setSearchTerms] = useState({
        asset: "",
        to_location: ""
    });
    const [showDropdowns, setShowDropdowns] = useState({
        asset: false,
        to_location: false
    });
    
    // Create refs for each dropdown
    const assetRef = useRef(null);
    const toLocationRef = useRef(null);

    // Modal form data for view/edit
    const [modalData, setModalData] = useState({
        asset_name: "",
        asset_code: "",
        from_location_name: "",
        from_location_id: "",
        to_location_name: "",
        to_location_id: "",
        movement_date: "",
        company_id: ""
    });

    // Get company ID from localStorage
    useEffect(() => {
        const getCompanyId = () => {
            console.log("Attempting to get company ID from various sources...");
            
            const directCompanyId = localStorage.getItem("company_id");
            if (directCompanyId) {
                console.log("Found company_id in localStorage:", directCompanyId);
                setCompanyId(directCompanyId);
                setCreateFormData(prev => ({
                    ...prev,
                    company_id: directCompanyId
                }));
                return;
            }
            
            const companyInfo = localStorage.getItem("companyInfo");
            if (companyInfo) {
                try {
                    const parsed = JSON.parse(companyInfo);
                    const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                    if (id) {
                        setCompanyId(String(id));
                        setCreateFormData(prev => ({
                            ...prev,
                            company_id: String(id)
                        }));
                        console.log("Using company ID from companyInfo:", id);
                        return;
                    }
                } catch (error) {
                    console.error("Error parsing companyInfo:", error);
                }
            }
            
            const userData = localStorage.getItem("user");
            if (userData) {
                try {
                    const parsed = JSON.parse(userData);
                    
                    if (parsed.company_id) {
                        setCompanyId(parsed.company_id);
                        setCreateFormData(prev => ({
                            ...prev,
                            company_id: parsed.company_id
                        }));
                        console.log("Using direct company_id from user:", parsed.company_id);
                        return;
                    }
                    
                    if (parsed.company) {
                        const companyId = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                        if (companyId) {
                            setCompanyId(String(companyId));
                            setCreateFormData(prev => ({
                                ...prev,
                                company_id: String(companyId)
                            }));
                            console.log("Using company ID from user.company:", companyId);
                            return;
                        }
                    }
                    
                    if (parsed.companies && Array.isArray(parsed.companies) && parsed.companies.length > 0) {
                        const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1);
                        if (mainCompany) {
                            const companyId = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                setCreateFormData(prev => ({
                                    ...prev,
                                    company_id: String(companyId)
                                }));
                                console.log("Using main company ID from companies array:", companyId);
                                return;
                            }
                        }
                        
                        const firstCompany = parsed.companies[0];
                        if (firstCompany) {
                            const companyId = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                setCreateFormData(prev => ({
                                    ...prev,
                                    company_id: String(companyId)
                                }));
                                console.log("Using first company ID from companies array:", companyId);
                                return;
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error parsing user data:", error);
                }
            }
            
            console.warn("No company ID found in any source");
        };
        
        getCompanyId();
    }, []);

    // Validate that from and to locations are different
    useEffect(() => {
        if (createFormData.from_location_id && createFormData.to_location_id) {
            if (createFormData.from_location_id === createFormData.to_location_id) {
                setLocationError("From and To locations must be different");
            } else {
                setLocationError("");
            }
        } else {
            setLocationError("");
        }
    }, [createFormData.from_location_id, createFormData.to_location_id]);

    // When asset is selected, fetch its current location
    useEffect(() => {
        const fetchAssetCurrentLocation = async () => {
            if (createFormData.asset_id && companyId) {
                try {
                    // You might need an API endpoint to get asset's current location
                    // For now, we'll check if the asset has a location_id property
                    const selectedAsset = assets.find(a => a.id === createFormData.asset_id);
                    if (selectedAsset && selectedAsset.location_id) {
                        const location = locations.find(l => l.id === selectedAsset.location_id);
                        if (location) {
                            setCurrentAssetLocation(location);
                            setCreateFormData(prev => ({
                                ...prev,
                                from_location_id: location.id
                            }));
                        }
                    } else {
                        setCurrentAssetLocation(null);
                        setCreateFormData(prev => ({
                            ...prev,
                            from_location_id: ""
                        }));
                    }
                } catch (error) {
                    console.error("Error fetching asset location:", error);
                }
            } else {
                setCurrentAssetLocation(null);
                setCreateFormData(prev => ({
                    ...prev,
                    from_location_id: ""
                }));
            }
        };

        fetchAssetCurrentLocation();
    }, [createFormData.asset_id, assets, locations, companyId]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assetRef.current && !assetRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, asset: false }));
            }
            if (toLocationRef.current && !toLocationRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, to_location: false }));
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch data for create modal when opened
    useEffect(() => {
        if (showCreateModal) {
            if (companyId) {
                fetchAssets();
                fetchLocations();
            } else {
                console.warn("Cannot fetch data: Company ID not available");
                Swal.fire({
                    icon: 'warning',
                    title: 'Company ID Missing',
                    text: 'Please select a company and try again.',
                    confirmButtonText: 'Go to Dashboard'
                }).then(() => {
                    setShowCreateModal(false);
                    navigate('/dashboard');
                });
            }
        }
    }, [showCreateModal, companyId]);

    // Fetch asset movements when companyId is available
    useEffect(() => {
        if (companyId) {
            console.log("Company ID available, fetching asset movements for company:", companyId);
            fetchAssetMovements();
        } else {
            console.warn("Company ID not available yet, waiting...");
        }
    }, [companyId]);

    const fetchAssetMovements = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                console.error("No auth token found");
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            if (!companyId) {
                console.error("Company ID is required");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please select a company and try again.'
                });
                setLoading(false);
                return;
            }

            console.log(`Fetching asset movements for company ID: ${companyId}`);
            
            const response = await axios.get(`${BASE_URL}/v1/assetmovement`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    company_id: companyId
                }
            });

            console.log("Asset movements response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                setAssetMovements(response.data.data);
                
                // Count unique assets
                const uniqueAssetIds = new Set(response.data.data.map(m => m.asset_id));
                
                setSelectedMetrics({
                    total_movements: response.data.data.length,
                    unique_assets: uniqueAssetIds.size
                });
                
                console.log(`Loaded ${response.data.data.length} asset movements`);
            } else {
                console.warn("Unexpected response format:", response.data);
                setAssetMovements([]);
            }
        } catch (error) {
            console.error("Error fetching asset movements:", error);
            
            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);
                
                let errorMessage = "Failed to fetch asset movements";
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 401) {
                    errorMessage = "Unauthorized. Please log in again.";
                } else if (error.response.status === 404) {
                    errorMessage = "API endpoint not found";
                }
                
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            } else if (error.request) {
                console.error("No response received:", error.request);
                Swal.fire({
                    icon: 'error',
                    title: 'Network Error',
                    text: 'No response from server. Please check your connection.'
                });
            } else {
                console.error("Error setting up request:", error.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occurred while setting up the request'
                });
            }
            
            setAssetMovements([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssets = async () => {
        setCreateLoading(prev => ({ ...prev, assets: true }));
        try {
            const token = localStorage.getItem("authToken");
            
            if (!companyId) {
                console.error("Cannot fetch assets: No company ID");
                return;
            }
            
            const response = await axios.get(`${BASE_URL}/v1/fixedassets/fetch`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: companyId }
            });
            
            console.log("Assets response:", response.data);
            
            if (response.data.status && Array.isArray(response.data.data)) {
                setAssets(response.data.data);
                console.log(`Loaded ${response.data.data.length} assets`);
            } else {
                console.warn("Unexpected assets response format:", response.data);
                setAssets([]);
            }
        } catch (error) {
            console.error("Error fetching assets:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch assets. Please try again.'
            });
        } finally {
            setCreateLoading(prev => ({ ...prev, assets: false }));
        }
    };

    const fetchLocations = async () => {
        setCreateLoading(prev => ({ ...prev, locations: true }));
        try {
            const token = localStorage.getItem("authToken");
            
            if (!companyId) {
                console.error("Cannot fetch locations: No company ID");
                return;
            }
            
            const response = await axios.get(`${BASE_URL}/v1/assetlocation/fetch`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: companyId }
            });
            
            console.log("Locations response:", response.data);
            
            if (response.data.status && Array.isArray(response.data.data)) {
                setLocations(response.data.data);
                console.log(`Loaded ${response.data.data.length} locations`);
            } else {
                console.warn("Unexpected locations response format:", response.data);
                setLocations([]);
            }
        } catch (error) {
            console.error("Error fetching locations:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch locations. Please try again.'
            });
        } finally {
            setCreateLoading(prev => ({ ...prev, locations: false }));
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
        '/fixed-assets': 'Fixed Assets',
    };

    // Process movement data for modal
    const processMovementData = (movement) => {
        console.log("Processing movement data:", movement);
        
        setModalData({
            id: movement.id,
            asset_name: movement.asset?.asset_name || movement.asset_name || "N/A",
            asset_code: movement.asset?.asset_code || movement.asset_code || "N/A",
            from_location_name: movement.from_location?.location_name || movement.from_location_name || "N/A",
            from_location_id: movement.from_location_id || "",
            to_location_name: movement.to_location?.location_name || movement.to_location_name || "N/A",
            to_location_id: movement.to_location_id || "",
            movement_date: movement.movement_date || "",
            company_id: movement.company_id || ""
        });
    };

    // Handle View/Edit icon clicks
    const handleViewClick = async (movement) => {
        setModalMode("view");
        setSelectedMovement(movement);
        setModalLoading(true);
        setShowViewEditModal(true);
        processMovementData(movement);
        setModalLoading(false);
    };

    const handleEditClick = async (movement) => {
        setModalMode("edit");
        setSelectedMovement(movement);
        setModalLoading(true);
        setShowViewEditModal(true);
        processMovementData(movement);
        setModalLoading(false);
    };

    // Handle modal input changes for view/edit
    const handleModalChange = (field, value) => {
        setModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle create form changes
    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle searchable select for create modal
    const handleSearchChange = (field, e) => {
        setSearchTerms(prev => ({ ...prev, [field]: e.target.value }));
        if (!showDropdowns[field]) {
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
        }
    };

    const handleSelect = (field, item) => {
        if (field === 'asset') {
            setCreateFormData(prev => ({
                ...prev,
                asset_id: item.id
            }));
            
            const displayName = `${item.asset_name} (${item.asset_code})`;
            setSearchTerms(prev => ({ ...prev, asset: displayName }));
            setShowDropdowns(prev => ({ ...prev, asset: false }));
        } else if (field === 'to_location') {
            setCreateFormData(prev => ({
                ...prev,
                to_location_id: item.id
            }));
            
            setSearchTerms(prev => ({ ...prev, to_location: item.location_name }));
            setShowDropdowns(prev => ({ ...prev, to_location: false }));
        }
    };

    const getSelectedName = (field) => {
        if (field === 'asset') {
            const id = createFormData.asset_id;
            if (!id) return "";
            
            const item = assets.find(i => i.id === id);
            if (!item) return "";
            
            return `${item.asset_name} (${item.asset_code})`;
        } else if (field === 'to_location') {
            const id = createFormData.to_location_id;
            if (!id) return "";
            
            const item = locations.find(i => i.id === id);
            if (!item) return "";
            
            return item.location_name || "";
        }
        return "";
    };

    const filterItems = (field) => {
        const searchTerm = (searchTerms[field] || "").toLowerCase();
        
        if (field === 'asset') {
            if (!Array.isArray(assets)) return [];
            
            return assets.filter(item => {
                if (!item) return false;
                
                const assetName = (item.asset_name || "").toLowerCase();
                const assetCode = (item.asset_code || "").toLowerCase();
                return assetName.includes(searchTerm) || assetCode.includes(searchTerm);
            });
        } else if (field === 'to_location') {
            if (!Array.isArray(locations)) return [];
            
            return locations.filter(item => {
                if (!item) return false;
                
                const locationName = (item.location_name || "").toLowerCase();
                return locationName.includes(searchTerm);
            });
        }
        return [];
    };

    const handleSave = async () => {
        if (modalMode === "edit") {
            setSaveLoading(true);
            try {
                const token = localStorage.getItem("authToken");

                // Double-check company ID
                let currentCompanyId = modalData.company_id || companyId;
                
                if (!currentCompanyId) {
                    currentCompanyId = localStorage.getItem("company_id");
                }

                if (!currentCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Company ID Error',
                        text: 'Company ID not found. Please select a company and try again.'
                    });
                    setSaveLoading(false);
                    return;
                }

                // Validate locations are different
                if (modalData.from_location_id && modalData.to_location_id && 
                    modalData.from_location_id === modalData.to_location_id) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Validation Error',
                        text: 'From and To locations must be different'
                    });
                    setSaveLoading(false);
                    return;
                }

                // For edit mode
                const requestData = {
                    from_location_id: modalData.from_location_id || null,
                    to_location_id: modalData.to_location_id,
                    movement_date: modalData.movement_date,
                    company_id: currentCompanyId
                };

                console.log("Updating asset movement with data:", requestData);

                const response = await axios.post(
                    `${BASE_URL}/v1/assetmovement/update/${selectedMovement.id}`,
                    requestData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                console.log("Update response:", response.data);

                if (response.data.status) {
                    fetchAssetMovements();
                    setShowViewEditModal(false);

                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: response.data.message || 'Asset movement updated successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    let errorMessage = "Failed to update asset movement";
                    if (response.data.message) {
                        if (typeof response.data.message === 'object') {
                            errorMessage = Object.values(response.data.message).flat().join(', ');
                        } else {
                            errorMessage = response.data.message;
                        }
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: errorMessage
                    });
                }
            } catch (error) {
                console.error("Error updating asset movement:", error);

                let errorMessage = "Failed to update asset movement";

                if (error.response) {
                    if (error.response.data && error.response.data.message) {
                        if (typeof error.response.data.message === 'object') {
                            errorMessage = Object.values(error.response.data.message).flat().join(', ');
                        } else {
                            errorMessage = error.response.data.message;
                        }
                    } else if (error.response.status === 422) {
                        errorMessage = "Validation error. Please check all required fields.";
                    }
                } else if (error.request) {
                    errorMessage = "No response from server. Please check your connection.";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            } finally {
                setSaveLoading(false);
            }
        } else {
            setShowViewEditModal(false);
        }
    };

    // Handle create submit
    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.asset_id || 
            !createFormData.to_location_id || 
            !createFormData.movement_date) {
            Swal.fire("Error", "Please fill in all required fields", "error");
            return;
        }

        // Validate locations are different
        if (createFormData.from_location_id && createFormData.to_location_id && 
            createFormData.from_location_id === createFormData.to_location_id) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'From and To locations must be different'
            });
            return;
        }

        // Double-check company ID
        let currentCompanyId = companyId;
        
        if (!currentCompanyId) {
            currentCompanyId = localStorage.getItem("company_id");
            
            if (!currentCompanyId) {
                const companyInfo = localStorage.getItem("companyInfo");
                if (companyInfo) {
                    try {
                        const parsed = JSON.parse(companyInfo);
                        currentCompanyId = parsed?.id || parsed?.company_id || parsed?.companyId;
                    } catch (error) {
                        console.error("Error parsing companyInfo:", error);
                    }
                }
            }
            
            if (currentCompanyId) {
                setCompanyId(currentCompanyId);
            }
        }

        if (!currentCompanyId) {
            Swal.fire({
                icon: 'error',
                title: 'Company ID Error',
                text: 'Company ID not found. Please select a company and try again.',
                confirmButtonText: 'Go to Dashboard'
            }).then(() => {
                navigate('/dashboard');
            });
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const payload = {
                asset_id: createFormData.asset_id,
                from_location_id: createFormData.from_location_id || null,
                to_location_id: createFormData.to_location_id,
                movement_date: createFormData.movement_date,
                company_id: currentCompanyId
            };

            console.log("Creating asset movement with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/v1/assetmovement/create`,
                payload,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    } 
                }
            );

            console.log("Create response:", response.data);

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Asset movement recorded successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });
                
                resetCreateForm();
                onClose();
                refreshMovements();
            } else {
                Swal.fire("Error", response.data?.message || "Failed to record asset movement", "error");
            }
        } catch (error) {
            console.error("Error creating asset movement:", error);
            
            let errorMessage = "Something went wrong while recording the movement";
            if (error.response?.data?.message) {
                if (typeof error.response.data.message === 'object') {
                    errorMessage = Object.values(error.response.data.message).flat().join(', ');
                } else {
                    errorMessage = error.response.data.message;
                }
            }
            
            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete movement with SweetAlert
    const handleDeleteClick = async (movement) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                console.log(`Deleting asset movement with ID: ${movement.id}`);
                
                const response = await axios.delete(
                    `${BASE_URL}/v1/assetmovement/${movement.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        params: {
                            company_id: companyId
                        }
                    }
                );

                console.log("Delete response:", response.data);

                if (response.data.status) {
                    fetchAssetMovements();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Asset movement deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || "Failed to delete asset movement");
                }
            } catch (error) {
                console.error("Error deleting asset movement:", error);

                let errorMessage = "Failed to delete asset movement";
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    const resetCreateForm = () => {
        setCreateFormData({
            asset_id: "",
            from_location_id: "",
            to_location_id: "",
            movement_date: "",
            company_id: companyId
        });
        setSearchTerms({
            asset: "",
            to_location: ""
        });
        setCurrentAssetLocation(null);
        setLocationError("");
    };

    const onClose = () => {
        setShowCreateModal(false);
        resetCreateForm();
        setShowDropdowns({
            asset: false,
            to_location: false
        });
    };

    const refreshMovements = () => {
        fetchAssetMovements();
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString();
    };

    // Skeleton loader for table rows
    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    // Skeleton for modal form inputs
    const ModalInputSkeleton = () => (
        <div className={classes.skeletonInput}></div>
    );

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname] || "Fixed Assets"} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assets')}>
                            Assets
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/categories')}>
                            Categories
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/locations')}>
                            Locations
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assignments')}>
                            Assignments
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/depreciation')}>
                            Depreciation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/revaluation')}>
                            Revaluation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/disposal')}>
                            Disposal
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/maintenance')}>
                            Maintenance
                        </button>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Movements
                        </button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Asset Movements</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Movements</p>
                                    <p className={classes.kdptg1}>
                                        {selectedMetrics?.total_movements?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Asset transfers</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Unique Assets</p>
                                    <p className={classes.kdptg2}>
                                        {selectedMetrics?.unique_assets?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Assets moved</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search movements" />
                            </div>
                            <div className={classes.selectWrapper}>
                                <select className={classes.dropdown}>
                                    <option value="">All Assets</option>
                                    {assets.map(asset => (
                                        <option key={asset.id} value={asset.id}>{asset.asset_name}</option>
                                    ))}
                                </select>
                                <span className={classes.dropdownArrow}>▼</span>
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>

                        <button className={classes.btnss} onClick={() => setShowCreateModal(true)}>
                            + Record Movement
                        </button>
                    </div>

                    <table className={classes.invoiceTable}>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Asset</th>
                                <th>From Location</th>
                                <th>To Location</th>
                                <th>Movement Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton />
                            ) : assetMovements.length > 0 ? (
                                assetMovements.map((movement, index) => (
                                    <tr key={movement.id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            {movement.fixedasset?.asset_name || "—"}
                                            {movement.asset?.asset_code && <div className={classes.smallText}>{movement.asset.asset_code}</div>}
                                        </td>
                                        <td>{movement.from_location?.location_name || movement.from_location_name || "—"}</td>
                                        <td>{movement.to_location?.location_name || movement.to_location_name || "—"}</td>
                                        <td>{formatDate(movement.movement_date)}</td>
                                        <td>
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                <Eye
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#007bff" }}
                                                    onClick={() => handleViewClick(movement)}
                                                />
                                                <Pencil
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#28a745" }}
                                                    onClick={() => handleEditClick(movement)}
                                                />
                                                <Trash2
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#dc3545" }}
                                                    onClick={() => handleDeleteClick(movement)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                                        {loading ? "Loading..." : "No asset movement records found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View/Edit Modal */}
            {showViewEditModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <Move className={classes.icon} />
                                <h2>{modalMode === "edit" ? "Edit Movement Record" : "View Movement Record"}</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewEditModal(false)} disabled={saveLoading}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.formGrid}>
                            {modalLoading ? (
                                Array.from({ length: 5 }).map((_, index) => (
                                    <div className={classes.formGroup} key={index}>
                                        <div className={classes.skeletonLabel}></div>
                                        <ModalInputSkeleton />
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className={classes.formGroup}>
                                        <label>Asset</label>
                                        <input
                                            type="text"
                                            value={`${modalData.asset_name} (${modalData.asset_code})`}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>From Location</label>
                                        <input
                                            type="text"
                                            value={modalData.from_location_name}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>To Location *</label>
                                        {modalMode === "edit" ? (
                                            <div className={classes.searchSelectWrapper} ref={toLocationRef}>
                                                <div className={classes.searchInputContainer}>
                                                    <input
                                                        type="text"
                                                        className={classes.searchSelectInput}
                                                        placeholder="Search and select to location..."
                                                        value={showDropdowns.to_location ? searchTerms.to_location : (modalData.to_location_name !== "N/A" ? modalData.to_location_name : "")}
                                                        onChange={(e) => handleSearchChange('to_location', e)}
                                                        onClick={() => setShowDropdowns(prev => ({ ...prev, to_location: true }))}
                                                        onFocus={() => setShowDropdowns(prev => ({ ...prev, to_location: true }))}
                                                        disabled={saveLoading}
                                                    />
                                                </div>

                                                {showDropdowns.to_location && (
                                                    <div className={classes.searchDropdown}>
                                                        <div className={classes.dropdownList}>
                                                            {createLoading.locations ? (
                                                                <div className={classes.dropdownItem}>Loading locations...</div>
                                                            ) : filterItems('to_location').length === 0 ? (
                                                                <div className={classes.dropdownItem}>No locations found</div>
                                                            ) : (
                                                                filterItems('to_location').map((item) => (
                                                                    <div
                                                                        key={item.id}
                                                                        className={`${classes.dropdownItem} ${modalData.to_location_id === item.id ? classes.selectedItem : ''}`}
                                                                        onClick={() => {
                                                                            handleSelect('to_location', item);
                                                                            handleModalChange('to_location_id', item.id);
                                                                            handleModalChange('to_location_name', item.location_name);
                                                                        }}
                                                                    >
                                                                        {item.location_name}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                value={modalData.to_location_name}
                                                readOnly
                                                className={classes.Field2}
                                            />
                                        )}
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Movement Date *</label>
                                        <input
                                            type={modalMode === "edit" ? "date" : "text"}
                                            value={modalData.movement_date}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("movement_date", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2}
                                        />
                                    </div>

                                    <input type="hidden" value={modalData.company_id} />
                                </>
                            )}
                        </div>

                        {modalMode === "edit" && modalData.from_location_id && modalData.to_location_id && 
                         modalData.from_location_id === modalData.to_location_id && (
                            <div className={classes.errorMessage}>
                                From and To locations must be different
                            </div>
                        )}

                        <button
                            className={classes.saveBtn}
                            onClick={handleSave}
                            disabled={saveLoading || (modalMode === "edit" && modalData.from_location_id && modalData.to_location_id && 
                                    modalData.from_location_id === modalData.to_location_id)}
                        >
                            {saveLoading ? (
                                <span className={classes.loadingText}>
                                    <span className={classes.spinner}></span>
                                    Saving...
                                </span>
                            ) : (
                                modalMode === "edit" ? "Update Record" : "Close"
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <Move className={classes.icon} />
                                <h2>Record Asset Movement</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={onClose}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit}>
                            <div className={classes.formGrid}>
                                <div className={classes.formGroup}>
                                    <label>Select Asset *</label>
                                    <div className={classes.searchSelectWrapper} ref={assetRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select asset..."
                                                value={showDropdowns.asset ? searchTerms.asset : getSelectedName('asset')}
                                                onChange={(e) => handleSearchChange('asset', e)}
                                                onClick={() => setShowDropdowns(prev => ({ ...prev, asset: true }))}
                                                onFocus={() => setShowDropdowns(prev => ({ ...prev, asset: true }))}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.asset && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.assets ? (
                                                        <div className={classes.dropdownItem}>Loading assets...</div>
                                                    ) : filterItems('asset').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No assets found</div>
                                                    ) : (
                                                        filterItems('asset').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.asset_id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('asset', item)}
                                                            >
                                                                {item.asset_name} ({item.asset_code})
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>From Location</label>
                                    <input
                                        type="text"
                                        value={currentAssetLocation?.location_name || "No location found for this asset"}
                                        readOnly
                                        className={`${classes.Field2} ${!currentAssetLocation ? classes.warningField : ''}`}
                                    />
                                    {!currentAssetLocation && createFormData.asset_id && (
                                        <small className={classes.warningText}>This asset doesn't have a current location</small>
                                    )}
                                </div>

                                <div className={classes.formGroup}>
                                    <label>To Location *</label>
                                    <div className={classes.searchSelectWrapper} ref={toLocationRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select to location..."
                                                value={showDropdowns.to_location ? searchTerms.to_location : getSelectedName('to_location')}
                                                onChange={(e) => handleSearchChange('to_location', e)}
                                                onClick={() => setShowDropdowns(prev => ({ ...prev, to_location: true }))}
                                                onFocus={() => setShowDropdowns(prev => ({ ...prev, to_location: true }))}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.to_location && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.locations ? (
                                                        <div className={classes.dropdownItem}>Loading locations...</div>
                                                    ) : filterItems('to_location').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No locations found</div>
                                                    ) : (
                                                        filterItems('to_location').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.to_location_id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('to_location', item)}
                                                            >
                                                                {item.location_name}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Movement Date *</label>
                                    <input
                                        type="date"
                                        name="movement_date"
                                        value={createFormData.movement_date}
                                        onChange={handleCreateChange}
                                        required
                                    />
                                </div>
                            </div>

                            {locationError && (
                                <div className={classes.errorMessage}>
                                    {locationError}
                                </div>
                            )}

                            <button 
                                type="submit" 
                                className={classes.saveBtn} 
                                disabled={submitting || !!locationError}
                            >
                                {submitting ? "Recording..." : "Record Movement"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AssetMovement;